import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Eye, 
  EyeOff, 
  Star, 
  Save, 
  X, 
  Settings,
  Users,
  BarChart3,
  FileText
} from 'lucide-react';
import axios from 'axios';
import AdminAboutSection from './AdminAboutSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Simple admin authentication (in production, use proper auth)
  const ADMIN_PASSWORD = 'Optician7!5923';

  const [newTestimonial, setNewTestimonial] = useState({
    name: '',
    company: '',
    rating: 5,
    text: '',
    avatar: '',
    is_featured: false
  });

  // Site settings state
  const [siteSettings, setSiteSettings] = useState({
    booking_enabled: false,
    booking_message: 'Schedule your meeting with LOVEYUHLIFE today!',
    coming_soon_message: 'Coming Soon'
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const handleLogin = () => {
    if (adminPassword === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAdminPassword('');
    } else {
      alert('Invalid password');
    }
  };

  const fetchData = async () => {
    try {
      const [testimonialsRes, servicesRes, bookingsRes, settingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/testimonials/all`),
        axios.get(`${BACKEND_URL}/api/services`),
        axios.get(`${BACKEND_URL}/api/bookings`),
        axios.get(`${BACKEND_URL}/api/settings`)
      ]);
      
      setTestimonials(testimonialsRes.data);
      setServices(servicesRes.data);
      setBookings(bookingsRes.data);
      setSiteSettings(settingsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleAddTestimonial = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/testimonials`, newTestimonial);
      setTestimonials([...testimonials, response.data]);
      setNewTestimonial({
        name: '',
        company: '',
        rating: 5,
        text: '',
        avatar: '',
        is_featured: false
      });
      setShowAddForm(false);
      alert('Testimonial added successfully!');
    } catch (error) {
      console.error('Error adding testimonial:', error);
      alert('Error adding testimonial');
    }
  };

  const handleUpdateTestimonial = async (id, updatedData) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/testimonials/${id}`, updatedData);
      setTestimonials(testimonials.map(t => t.id === id ? response.data : t));
      setEditingTestimonial(null);
      alert('Testimonial updated successfully!');
    } catch (error) {
      console.error('Error updating testimonial:', error);
      alert('Error updating testimonial');
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        await axios.delete(`${BACKEND_URL}/api/testimonials/${id}`);
        setTestimonials(testimonials.filter(t => t.id !== id));
        alert('Testimonial deleted successfully!');
      } catch (error) {
        console.error('Error deleting testimonial:', error);
        alert('Error deleting testimonial');
      }
    }
  };

  const handleDeleteBooking = async (id) => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      try {
        await axios.delete(`${BACKEND_URL}/api/bookings/${id}`);
        setBookings(bookings.filter(b => b.id !== id));
        alert('Booking deleted successfully!');
      } catch (error) {
        console.error('Error deleting booking:', error);
        alert('Error deleting booking');
      }
    }
  };

  const handleUpdateSiteSettings = async (newSettings) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/settings`, newSettings);
      setSiteSettings(response.data);
      alert('Site settings updated successfully!');
    } catch (error) {
      console.error('Error updating site settings:', error);
      alert('Error updating site settings');
    }
  };

  const toggleTestimonialVisibility = async (testimonial) => {
    const updatedTestimonial = { ...testimonial, is_featured: !testimonial.is_featured };
    await handleUpdateTestimonial(testimonial.id, updatedTestimonial);
  };

  const generateAvatar = (name) => {
    const names = name.split(' ');
    return names.map(n => n.charAt(0).toUpperCase()).join('').substring(0, 2);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={16}
        className={index < rating ? 'fill-current text-yellow-400' : 'text-gray-300'}
      />
    ));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-center">LOVEYUHLIFE Admin</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              type="password"
              placeholder="Enter admin password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              className="mb-4"
            />
            <Button onClick={handleLogin} className="w-full">
              Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">LOVEYUHLIFE Admin Panel</h1>
          <Button onClick={() => setIsAuthenticated(false)} variant="outline">
            Logout
          </Button>
        </div>

        <Tabs defaultValue="testimonials" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="testimonials" className="flex items-center gap-2">
              <Users size={16} />
              Testimonials
            </TabsTrigger>
            <TabsTrigger value="about" className="flex items-center gap-2">
              <FileText size={16} />
              About
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2">
              <Settings size={16} />
              Services
            </TabsTrigger>
            <TabsTrigger value="bookings" className="flex items-center gap-2">
              <BarChart3 size={16} />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings size={16} />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Testimonials Management */}
          <TabsContent value="testimonials">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">Manage Testimonials</h2>
                <Button onClick={() => setShowAddForm(true)} className="flex items-center gap-2">
                  <Plus size={16} />
                  Add New Testimonial
                </Button>
              </div>

              <div className="grid gap-4">
                <div className="text-sm text-gray-600 mb-4">
                  <Badge variant="secondary">Total: {testimonials.length}</Badge>
                  <Badge variant="default" className="ml-2">Visible: {testimonials.filter(t => t.is_featured).length}</Badge>
                  <Badge variant="outline" className="ml-2">Hidden: {testimonials.filter(t => !t.is_featured).length}</Badge>
                </div>

                {/* Add New Testimonial Form */}
                {showAddForm && (
                  <Card className="border-green-200">
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle>Add New Testimonial</CardTitle>
                        <Button onClick={() => setShowAddForm(false)} variant="ghost" size="sm">
                          <X size={16} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          placeholder="Client Name"
                          value={newTestimonial.name}
                          onChange={(e) => setNewTestimonial({...newTestimonial, name: e.target.value})}
                        />
                        <Input
                          placeholder="Company Name"
                          value={newTestimonial.company}
                          onChange={(e) => setNewTestimonial({...newTestimonial, company: e.target.value})}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Rating</label>
                          <select 
                            value={newTestimonial.rating}
                            onChange={(e) => setNewTestimonial({...newTestimonial, rating: parseInt(e.target.value)})}
                            className="w-full p-2 border rounded-md"
                          >
                            {[1,2,3,4,5].map(rating => (
                              <option key={rating} value={rating}>{rating} Stars</option>
                            ))}
                          </select>
                        </div>
                        <Input
                          placeholder="Avatar (e.g., JD)"
                          value={newTestimonial.avatar}
                          onChange={(e) => setNewTestimonial({...newTestimonial, avatar: e.target.value})}
                        />
                      </div>
                      <Textarea
                        placeholder="Testimonial text..."
                        value={newTestimonial.text}
                        onChange={(e) => setNewTestimonial({...newTestimonial, text: e.target.value})}
                        rows={4}
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="featured"
                          checked={newTestimonial.is_featured}
                          onChange={(e) => setNewTestimonial({...newTestimonial, is_featured: e.target.checked})}
                        />
                        <label htmlFor="featured">Make visible on website</label>
                      </div>
                      <Button onClick={handleAddTestimonial} className="w-full">
                        <Save size={16} className="mr-2" />
                        Add Testimonial
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Testimonials List */}
                {testimonials.map((testimonial) => (
                  <Card key={testimonial.id} className={`${testimonial.is_featured ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold">
                            {testimonial.avatar}
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{testimonial.name}</h3>
                            <p className="text-gray-600">{testimonial.company}</p>
                            <div className="flex items-center gap-1 mt-1">
                              {renderStars(testimonial.rating)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={testimonial.is_featured ? "default" : "secondary"}>
                            {testimonial.is_featured ? "Visible" : "Hidden"}
                          </Badge>
                          <Button
                            onClick={() => toggleTestimonialVisibility(testimonial)}
                            variant="outline"
                            size="sm"
                          >
                            {testimonial.is_featured ? <EyeOff size={16} /> : <Eye size={16} />}
                          </Button>
                          <Button
                            onClick={() => setEditingTestimonial(testimonial)}
                            variant="outline"
                            size="sm"
                          >
                            <Edit3 size={16} />
                          </Button>
                          <Button
                            onClick={() => handleDeleteTestimonial(testimonial.id)}
                            variant="destructive"
                            size="sm"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                      <p className="text-gray-700 italic">"{testimonial.text}"</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* About Section Management */}
          <TabsContent value="about">
            <AdminAboutSection />
          </TabsContent>

          {/* Services Overview */}
          <TabsContent value="services">
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Services Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {services.map((service) => (
                  <Card key={service.id}>
                    <CardContent className="p-6">
                      <h3 className="font-semibold mb-2">{service.title}</h3>
                      <p className="text-sm text-gray-600 mb-3">{service.description.substring(0, 100)}...</p>
                      <Badge variant={service.is_active ? "default" : "secondary"}>
                        {service.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Bookings Overview */}
          <TabsContent value="bookings">
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Meeting Bookings</h2>
              <div className="grid gap-4">
                {bookings.length === 0 ? (
                  <Card>
                    <CardContent className="p-6 text-center">
                      <p className="text-gray-600">No bookings yet</p>
                    </CardContent>
                  </Card>
                ) : (
                  bookings.map((booking) => (
                    <Card key={booking.id}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-lg">{booking.client_name}</h3>
                            <p className="text-gray-600">{booking.client_email}</p>
                            {booking.client_phone && (
                              <p className="text-gray-600">{booking.client_phone}</p>
                            )}
                            {booking.company && (
                              <p className="text-gray-600">{booking.company}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{booking.meeting_date}</p>
                            <p className="text-gray-600">{booking.meeting_time}</p>
                            <Badge variant={booking.status === 'scheduled' ? 'default' : 'secondary'}>
                              {booking.status}
                            </Badge>
                            <div className="mt-2">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteBooking(booking.id)}
                                className="text-xs"
                              >
                                <Trash2 size={14} className="mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                        {booking.message && (
                          <div className="mt-4 p-3 bg-gray-100 rounded">
                            <p className="text-sm">{booking.message}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings">
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Website Settings</h2>
              
              {/* Booking Settings */}
              <Card>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-2">Booking System Settings</h3>
                      <p className="text-sm text-gray-600 mb-4">Control whether visitors can book meetings</p>
                      
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="bookingEnabled"
                            checked={siteSettings.booking_enabled}
                            onChange={(e) => handleUpdateSiteSettings({ booking_enabled: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <label htmlFor="bookingEnabled" className="text-sm font-medium">
                            Enable Booking System
                          </label>
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium mb-1 block">Coming Soon Message:</label>
                          <Input
                            type="text"
                            value={siteSettings.coming_soon_message}
                            onChange={(e) => setSiteSettings({...siteSettings, coming_soon_message: e.target.value})}
                            onBlur={() => handleUpdateSiteSettings({ coming_soon_message: siteSettings.coming_soon_message })}
                            placeholder="Coming Soon"
                          />
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          Current Status: 
                          <span className={`ml-2 font-semibold ${siteSettings.booking_enabled ? 'text-green-600' : 'text-orange-600'}`}>
                            {siteSettings.booking_enabled ? 'Booking Active' : siteSettings.coming_soon_message}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-2">Testimonial Display Settings</h3>
                      <p className="text-sm text-gray-600 mb-4">Control how testimonials appear on your website</p>
                      <div className="space-y-2">
                        <Button 
                          onClick={() => {
                            testimonials.forEach(async (t) => {
                              if (t.is_featured) {
                                await handleUpdateTestimonial(t.id, {...t, is_featured: false});
                              }
                            });
                            setTimeout(fetchData, 1000);
                          }}
                          variant="outline"
                        >
                          Hide All Testimonials
                        </Button>
                        <Button 
                          onClick={() => {
                            testimonials.forEach(async (t) => {
                              if (!t.is_featured) {
                                await handleUpdateTestimonial(t.id, {...t, is_featured: true});
                              }
                            });
                            setTimeout(fetchData, 1000);
                          }}
                          variant="outline"
                          className="ml-2"
                        >
                          Show All Testimonials
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;